/**
 * ScanResultCard — compact card used in the continuous-scan stack and the
 * bulk label upload flow. Shows the AI extraction summary, the best catalog
 * match (if any), and quick-action buttons (legg til / manuelt).
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Badge, Button, Icon } from '@/design-system';
import { BottleImage } from '@/components/BottleCard';
import type { ScanResponse } from '@/lib/api/wines';
import type { VinmonopoletProduct } from '@/lib/api/types';

// ─── Shared helpers ───────────────────────────────────────────────────────────

export function extractionSummary(extraction: ScanResponse['extraction']): string {
  return [extraction.producer, extraction.productName, extraction.vintage]
    .filter(Boolean)
    .join(' · ') || extraction.searchQuery || 'Ukjent etikett';
}

export function extractionSubtitle(extraction: ScanResponse['extraction']): string {
  return [extraction.grape, extraction.region, extraction.country]
    .filter(Boolean)
    .join(' · ') || '';
}

export function extractionToPrefill(extraction: ScanResponse['extraction']): Record<string, unknown> {
  return {
    name: extraction.productName || extraction.name || extraction.searchQuery || '',
    producer: extraction.producer,
    vintage: extraction.vintage,
    region: extraction.region,
    country: extraction.country,
    grapeVarieties: extraction.grape
      ? extraction.grape.split(/[,;]/).map((s) => s.trim()).filter(Boolean)
      : extraction.grapeVarieties,
  };
}

// ─── Types ────────────────────────────────────────────────────────────────────

export type ScanCardStatus = 'scanning' | 'done' | 'error' | 'added';

export interface ScanCardData {
  /** Stable ID (e.g. file name hash or timestamp). */
  id: string;
  status: ScanCardStatus;
  /** Set when status=done or adding. */
  scanData?: ScanResponse;
  /** Error message when status=error. */
  errorMessage?: string;
  /** Set once the bottle has been added. */
  addedBottleId?: string;
  /** Preview data URL shown while scanning. */
  previewUrl?: string;
}

interface ScanResultCardProps {
  data: ScanCardData;
  labelQueueEnabled?: boolean;
  /** Called when user taps "Legg til" (best catalog match path). */
  onConfirmMatch?: (product: VinmonopoletProduct, vintage: string | undefined) => Promise<void>;
  /** Called when user taps "Legg til manuelt" — parent opens AddBottleDrawer. */
  onManualAdd?: (prefill: Record<string, unknown>) => void;
  /** Called when user dismisses/removes this card. */
  onDismiss?: () => void;
  /** Adding in progress for this card's best match product ID. */
  confirmingProductId?: string | null;
}

export function ScanResultCard({
  data,
  labelQueueEnabled = false,
  onConfirmMatch,
  onManualAdd,
  onDismiss,
  confirmingProductId,
}: ScanResultCardProps) {
  const [expanded, setExpanded] = useState(false);

  const ext = data.scanData?.extraction;
  const candidates = data.scanData?.candidates ?? [];
  const best = candidates[0];
  const bestProduct = best?.product;
  const isAdded = data.status === 'added';
  const isScanning = data.status === 'scanning';
  const isError = data.status === 'error';
  const isConfirming = confirmingProductId != null && bestProduct != null
    && confirmingProductId === bestProduct.basic.productId;

  const country = bestProduct
    ? (bestProduct.origins as { country?: string } | undefined)?.country
    : undefined;
  const region = bestProduct
    ? (bestProduct.origins as { region?: string } | undefined)?.region
    : undefined;

  const handleManual = () => {
    if (!ext) return;
    onManualAdd?.(extractionToPrefill(ext));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={[
        'ui-card grid gap-3 p-3',
        isAdded ? 'border-[color:var(--ui-color-success)]/40 bg-[color:var(--ui-color-success)]/5' : '',
        isError ? 'border-[color:var(--ui-color-danger)]/40' : '',
      ].join(' ')}
    >
      {/* Header row */}
      <div className="grid grid-cols-[auto_1fr_auto] items-start gap-3">
        {/* Thumbnail */}
        <div className="relative grid h-14 w-10 shrink-0 place-items-center overflow-hidden rounded bg-ink-soft">
          {data.previewUrl && !bestProduct ? (
            <img src={data.previewUrl} alt="" className="h-full w-full object-cover opacity-70" />
          ) : bestProduct ? (
            <BottleImage src={bestProduct.imageUrl} iconSize={14} />
          ) : (
            <Icon
              name={isScanning ? 'spinner' : isError ? 'warning' : 'camera'}
              className={[
                isScanning ? 'animate-spin text-mist' : '',
                isError ? 'text-[color:var(--ui-color-danger)]' : 'text-mist',
              ].join(' ')}
              size={18}
            />
          )}
          {isAdded && (
            <span className="absolute inset-0 grid place-items-center bg-[color:var(--ui-color-success)]/80">
              <Icon name="check" size={18} className="text-white" />
            </span>
          )}
        </div>

        {/* Text summary */}
        <div className="grid min-w-0 gap-0.5">
          {isScanning ? (
            <span className="text-sm text-mist">Analyserer etikett…</span>
          ) : isError ? (
            <>
              <span className="font-serif text-sm text-[color:var(--ui-color-danger)]">Skanning feilet</span>
              <span className="line-clamp-2 text-xs text-mist">{data.errorMessage}</span>
            </>
          ) : isAdded ? (
            <>
              <Badge tone="success" className="w-fit">Lagt i kjelleren</Badge>
              {ext && <span className="font-serif text-sm text-parchment">{extractionSummary(ext)}</span>}
            </>
          ) : (
            <>
              {ext && (
                <>
                  <span className="font-serif text-sm leading-tight text-parchment line-clamp-1">
                    {extractionSummary(ext)}
                  </span>
                  {extractionSubtitle(ext) && (
                    <span className="text-xs text-mist line-clamp-1">{extractionSubtitle(ext)}</span>
                  )}
                  {ext.confidence != null && (
                    <Badge tone="gold" className="w-fit text-[10px]">
                      AI {Math.round(ext.confidence * 100)}%
                    </Badge>
                  )}
                </>
              )}
              {bestProduct && (
                <span className="text-[11px] text-mist">
                  Treff: {bestProduct.basic.productLongName ?? bestProduct.basic.productShortName}
                  {(region ?? country) ? ` · ${region ?? country}` : ''}
                </span>
              )}
              {candidates.length === 0 && ext && (
                <Badge tone="danger" className="w-fit text-[10px]">Ingen katalogtref</Badge>
              )}
            </>
          )}
        </div>

        {/* Dismiss button */}
        {onDismiss && (
          <button
            type="button"
            className="ui-btn ui-btn--icon shrink-0"
            aria-label="Fjern"
            onClick={onDismiss}
          >
            <Icon name="x" size={12} />
          </button>
        )}
      </div>

      {/* Actions row — only show when scan is done and not yet added */}
      {!isScanning && !isError && !isAdded && (
        <div className="flex flex-wrap items-center gap-2">
          {bestProduct && onConfirmMatch && (
            <Button
              size="sm"
              variant="primary"
              iconLeft="add-bottle"
              loading={isConfirming}
              onClick={() => onConfirmMatch(bestProduct, ext?.vintage)}
              className="flex-1"
            >
              {labelQueueEnabled
                ? (candidates.length === 1 ? 'Legg til + QR' : 'Legg beste treff + QR')
                : (candidates.length === 1 ? 'Legg til' : 'Legg beste treff')}
            </Button>
          )}
          {ext && onManualAdd && (
            <Button
              size="sm"
              variant="ghost"
              iconLeft="edit"
              onClick={handleManual}
              className={bestProduct ? '' : 'flex-1'}
            >
              {bestProduct ? 'Manuelt' : 'Legg til manuelt'}
            </Button>
          )}
          {candidates.length > 1 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-[11px] uppercase tracking-widest text-mist hover:text-candle"
            >
              {expanded ? 'Skjul' : `${candidates.length - 1} til`}
            </button>
          )}
        </div>
      )}

      {/* Expanded alternative matches */}
      {expanded && candidates.length > 1 && (
        <div className="grid gap-1.5 pl-0">
          <span className="text-[10px] uppercase tracking-widest text-mist">Andre kandidater</span>
          {candidates.slice(1, 5).map((c) => {
            const cCountry = (c.product.origins as { country?: string } | undefined)?.country;
            const cRegion = (c.product.origins as { region?: string } | undefined)?.region;
            return (
              <button
                key={c.product.basic.productId}
                type="button"
                onClick={() => onConfirmMatch?.(c.product, ext?.vintage)}
                disabled={confirmingProductId != null}
                className="flex items-center gap-2 rounded-md border border-line p-2 text-left transition-colors hover:border-line-soft disabled:opacity-60"
              >
                <div className="relative grid h-10 w-8 shrink-0 place-items-center overflow-hidden rounded bg-ink-soft">
                  <BottleImage src={c.product.imageUrl} iconSize={12} />
                </div>
                <div className="grid min-w-0 flex-1">
                  <span className="font-serif text-xs text-parchment line-clamp-1">
                    {c.product.basic.productLongName ?? c.product.basic.productShortName}
                  </span>
                  <span className="text-[10px] text-mist">
                    {[c.product.basic.vintage, cRegion ?? cCountry].filter(Boolean).join(' · ')}
                  </span>
                </div>
                <Icon name="add-bottle" size={14} className="text-mist" />
              </button>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}
